/**
 * TaskTextEditor — rende il testo di una MIT/Azione come "click-to-edit" con:
 *   - Click sul testo → si trasforma in <input>; Enter salva, Esc annulla, blur salva
 *   - Icona 🗑️ cestino (visibile solo al hover del contenitore .group)
 *     con conferma nativa → delete hard dal DB
 *
 * Zero side-effect nel componente: le mutation sono passate come props dai
 * chiamanti (che gestiscono anche il reload / toast contestuale).
 *
 * Props:
 *   - kind: 'mit' | 'azione'
 *   - id: id della riga (parent per occurrence — non editabile)
 *   - isOccurrence: se true, edit e delete sono DISABILITATI
 *     (occorrenze ricorrenti richiedono modifica della serie via popover
 *     dedicato; qui evitiamo il rischio di "toccare la serie senza volerlo")
 *   - text: testo attuale
 *   - fatto: se true, applica line-through
 *   - onSaveText(newText) — deve gestire l'update via applyOperations + reload
 *   - onDelete() — deve gestire il delete via deleteMit/deleteAzione + reload
 *   - textClassName: classi extra sullo span testo (per line-through, opacity)
 *   - textStyle: style extra sullo span testo (per line-through, opacity)
 */
import { useEffect, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';

export default function TaskTextEditor({
  kind,
  id,
  isOccurrence = false,
  text,
  onSaveText,
  onDelete,
  textClassName = '',
  textStyle = {},
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Se il testo cambia dall'esterno (reload), aggiorna il draft
  useEffect(() => {
    if (!editing) setDraft(text || '');
  }, [text, editing]);

  const cancel = () => { setDraft(text || ''); setEditing(false); };

  const submit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === (text || '').trim()) { cancel(); return; }
    setSaving(true);
    try {
      await onSaveText(trimmed);
      setEditing(false);
    } catch {
      // Errore già mostrato dal chiamante via toast
      cancel();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!window.confirm(`Elimina definitivamente "${text}"?\n\nQuesta operazione NON è recuperabile.`)) return;
    onDelete();
  };

  if (editing && !isOccurrence) {
    return (
      <span className="flex-1 flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={submit}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          disabled={saving}
          maxLength={280}
          className="flex-1 font-body text-sm px-1 py-0.5 border-b border-ink bg-tint text-ink focus:outline-none"
          data-testid={`task-text-input-${kind}-${id}`}
        />
      </span>
    );
  }

  return (
    <>
      <span
        className={`flex-1 font-body text-sm ${isOccurrence ? '' : 'cursor-text hover:bg-tint hover:px-1 hover:-mx-1 transition-all rounded-sm'} ${textClassName}`}
        style={textStyle}
        onClick={(e) => {
          if (isOccurrence) return;
          e.stopPropagation();
          setEditing(true);
        }}
        title={isOccurrence
          ? 'Occorrenza ricorrente · modifica la serie dal pulsante 🔁'
          : 'Clicca per modificare il testo'}
        data-testid={`task-text-${kind}-${id}`}
      >
        {text}
      </span>
      {!isOccurrence && onDelete && (
        <button
          type="button"
          onClick={handleDeleteClick}
          className="shrink-0 p-1 text-mute hover:text-accent opacity-0 group-hover:opacity-100 transition-opacity"
          title="Elimina definitivamente"
          aria-label="Elimina"
          data-testid={`task-delete-${kind}-${id}`}
        >
          <Trash2 size={12} strokeWidth={1.6} />
        </button>
      )}
    </>
  );
}
